import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KiTranslatedBadge, StateChip } from '../../components/atoms.js';
import { PanelFieldInput } from '../../components/panel-fields.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { useScopedUser } from '../../lib/project-roles.js';
import {
  type ProcessDraft,
  emptyProcessDraft,
  processFields,
  toProcessDraft,
} from './process-fields.js';

// #28: Anlegen und Bearbeiten sind hier dieselbe Maske — sie koennen nicht auseinanderdriften.
// Ersetzt den frueheren NewProcessDialog (Modal), der nur zwei Felder kannte und kein
// Gegenstueck zum Bearbeiten hatte.

// C1: Bildet das Delta zwischen dem aktuellen Entwurf und dem geladenen Ausgangsstand — nur
// tatsaechlich geaenderte Felder wandern in den PATCH-Body. JSON.stringify statt !==, weil
// node_ids ein Array ist (ein Referenzvergleich waere hier fragil).
function diffDraft<T extends Record<string, unknown>>(draft: T, initial: T): Partial<T> {
  const patch: Partial<T> = {};
  for (const key of Object.keys(draft) as (keyof T)[]) {
    if (JSON.stringify(draft[key]) !== JSON.stringify(initial[key])) {
      patch[key] = draft[key];
    }
  }
  return patch;
}

export function ProcessPanel({
  projectId,
  processId,
  onSaved,
  onDeleted,
  onCancel,
  onDirtyChange,
}: {
  projectId: string;
  /** null ⇒ anlegen. */
  processId: string | null;
  onSaved: (processId: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  // I3: Speichern und Loeschen waren clientseitig nicht an catalog.edit gebunden — Muster wie
  // step-table.tsx/glossary-tab.tsx.
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  const [draft, setDraft] = useState<ProcessDraft | null>(processId ? null : emptyProcessDraft);
  const [initial, setInitial] = useState<ProcessDraft>(emptyProcessDraft);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const treeQuery = useQuery({
    queryKey: ['catalog-tree', projectId],
    queryFn: () => catalogApi.tree(projectId),
  });
  const processesQuery = useQuery({
    queryKey: ['catalog-processes', projectId],
    queryFn: () => catalogApi.listProcesses(projectId),
    enabled: processId !== null,
  });

  const streams = (treeQuery.data?.nodes ?? []).map((n) => ({ node_id: n.node_id, name: n.name }));
  const current = processesQuery.data?.find((p) => p.process_id === processId) ?? null;

  // Befuellt den Entwurf einmalig, sobald BEIDE Quellen da sind. Die Komponente wird ueber
  // `key={processId}` neu gemountet, wenn ein anderer Prozess gewaehlt wird — deshalb genuegt
  // die Bedingung „noch kein Entwurf" und es gibt keinen Reset bei jedem Refetch
  // (Lehre aus Paket B: ein Effekt am Objekt loescht sonst laufende Eingaben). Genau diese Sperre
  // (draft !== null) schuetzt weiterhin gegen die Refetch-Fehlerklasse: ein Refetch von current
  // oder treeQuery.data darf den schon befuellten Entwurf nicht mehr anfassen.
  //
  // Zusaetzlich noetig ist treeQuery.data !== undefined, weil `current` (aus processesQuery)
  // und treeQuery unabhaengig voneinander laden und in Produktion in beliebiger Reihenfolge
  // ankommen (der tree-Endpunkt aggregiert Knoten UND Prozesse und ist damit typischerweise
  // schwerer als die flache Prozessliste, kann also spaeter fertig werden). Ohne diese Bedingung
  // wuerde der Effekt genau einmal laufen, sobald `current` da ist, aber treeQuery.data noch
  // undefined ist — node_id wird dann dauerhaft auf null festgeschrieben, obwohl der Prozess
  // sehr wohl einem Teilprojekt zugeordnet ist. Speichert der Nutzer danach ohne das
  // Feld anzufassen, geht die Zuordnung serverseitig verloren (stiller Datenverlust).
  // Beide Bedingungen zusammen schuetzen also gegen zwei verschiedene Fehlerklassen: „kein
  // Reset bei Refetch" und „kein verfruehtes Festschreiben, bevor beide Quellen da sind".
  useEffect(() => {
    if (draft !== null || current === null || treeQuery.data === undefined) return;
    // #112: genau EIN Teilprojekt je Prozess — der Baum liefert seit der Sichtentrennung nur noch
    // Teilprojekt-Knoten, ein Prozess kann also hoechstens an einem davon haengen.
    const nodeId =
      (treeQuery.data?.nodes ?? []).find((n) =>
        n.processes.some((p) => p.process_id === current.process_id),
      )?.node_id ?? null;
    const loaded = toProcessDraft(current, nodeId);
    setDraft(loaded);
    setInitial(loaded);
  }, [current, draft, treeQuery.data]);

  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(initial);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['catalog-tree', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['catalog-processes', projectId] });
    // #143: Der gefilterte Baum liest seine Testfallzeilen aus der gecachten Volliste. Das Löschen
    // setzt test_cases.process_id per FK auf NULL, Speichern kann Zuordnungen ändern — ohne diese
    // Invalidierung verschwänden Testfälle im Filtermodus bis zu 30 s aus dem Baum.
    void queryClient.invalidateQueries({ queryKey: ['tcs-search', projectId] });
  };

  const create = useMutation({
    mutationFn: (d: ProcessDraft) => catalogApi.createProcess(projectId, d),
    meta: { successToast: 'feedback.created', errorToast: false },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, d }: { id: string; d: Partial<ProcessDraft> }) =>
      catalogApi.updateProcess(projectId, id, d),
    meta: { errorToast: false },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => catalogApi.deleteProcess(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: invalidate,
  });

  const save = async () => {
    // Zwei Dinge werden HIER geprueft, nicht nur am disabled-Knopf: das Recht (canEdit) und der
    // name_de-Pflichtfeld-Guard. Enter loest denselben Weg aus wie der Knopf, und ein Guard nur am
    // Knopf liesse die Tastatur trotzdem einen Request schicken — ohne Recht (Betrachter, 403 vom
    // Server) oder mit leerem Pflichtfeld (Befund aus der Task-4-Review, dort dieselbe Stelle).
    if (!canEdit || !draft || draft.name_de.trim() === '' || create.isPending || update.isPending)
      return;
    setError(null);
    try {
      // C1: Beim Bearbeiten nur die tatsaechlich geaenderten Felder schicken — derselbe Grund wie
      // in step-table.tsx: der Server loescht name_de_ai/name_en_ai schon bei blosser Anwesenheit
      // der jeweiligen Keys im Body (catalog-processes.ts). Ein Voll-Draft wuerde also z. B. beim
      // Nachtragen einer Notiz oder der SAP-Scope-Item-ID BEIDE Uebersetzungs-Abzeichen loeschen -
      // unwiederbringlich, denn gesetzt werden sie nur vom Uebersetzungslauf. Beim Anlegen (kein
      // `initial`-Vergleich sinnvoll) bleibt es beim vollen Draft.
      const saved =
        processId === null
          ? await create.mutateAsync(draft)
          : await update.mutateAsync({ id: processId, d: diffDraft(draft, initial) });
      // I2: initial muss den gerade gespeicherten Stand uebernehmen — sonst bleibt er auf dem
      // alten Wert stehen (der Befuellungs-Effekt ist ueber `draft !== null` gesperrt und laeuft
      // nach dem ersten Laden nie wieder), `dirty` bleibt intern true, und der Effekt an
      // [dirty, onDirtyChange] feuert bei der naechsten Aenderung nicht erneut (true -> true).
      // Das Eltern-Panel wuerde eine zweite Aenderung dann als NICHT dirty behandeln.
      setInitial(draft);
      onDirtyChange(false);
      onSaved(saved.process_id);
    } catch {
      setError(t('catalog.errors.generic'));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const multiline = (e.target as HTMLElement).tagName === 'TEXTAREA';
    if (e.key === 'Enter' && (!multiline || e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      void save();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDirtyChange(false);
      onCancel();
    }
  };

  if (!draft) return <div className="p-6 text-text-muted">{t('catalog.loading')}</div>;

  const fields = processFields((k) => t(k), streams);

  return (
    // Tastaturmuster des Panels, identisch zu EditRow in catalog-table.tsx.
    <div className="space-y-4 p-6" onKeyDown={onKeyDown}>
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold text-text-primary">
          {processId === null ? t('catalog.actions.addProcess') : t('catalog.actions.edit')}
        </h2>
        {/* #49 (LNG-4): Prüfmarkierung aus dem Import sichtbar machen (wie am Testfall, Screen 3a).
            Kommt IMMER vom Server (current) — nach einer manuellen Änderung löscht der Server das
            zugehörige *_en_ai-Flag, das UI liest nur nach. */}
        {current?.translation_review && (
          <StateChip label={t('catalog.translationReview')} tone="warn" />
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        {fields.map((field, index) => (
          // Kein <label>: PanelFieldInput setzt aria-label bereits selbst (Task 3), ein
          // umschliessendes <label> ohne htmlFor waere eine zweite, ungebundene Benennung.
          <div key={field.key} className="space-y-1">
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">
              {field.labelKey}
              {/* #49 (LNG-3) + Task 10 (#63/#64): KI-Übersetzt-Badge auf der jeweils per KI
                  gefuellten Sprachseite (DE-Quell-Import → name_en_ai, EN-Quell-Import →
                  name_de_ai). Verschwindet nach manueller Korrektur, weil der Server das Flag
                  löscht (LNG-4/HITL). */}
              {((field.key === 'name_en' && current?.name_en_ai) ||
                (field.key === 'name_de' && current?.name_de_ai)) && <KiTranslatedBadge />}
            </span>
            <PanelFieldInput
              field={field}
              draft={draft}
              onChange={setDraft}
              autoFocus={index === 0}
            />
            {field.kind === 'checkbox' && field.hintKey && (
              <span className="block text-xs text-text-muted">{field.hintKey}</span>
            )}
          </div>
        ))}
      </div>

      {error && <div className="text-xs text-status-fail">{error}</div>}

      {confirming ? (
        <div className="flex items-center gap-3">
          <span className="text-sm text-text-body">
            {t('catalog.confirm.deleteNamed', {
              name: pickLang(lang, draft.name_de, draft.name_en),
            })}
          </span>
          <button
            type="button"
            onClick={async () => {
              if (!processId) return;
              try {
                await remove.mutateAsync(processId);
                onDirtyChange(false);
                onDeleted();
              } catch {
                setError(t('catalog.errors.generic'));
                setConfirming(false);
              }
            }}
            disabled={remove.isPending}
            className="rounded-btn bg-status-fail px-3 py-1 text-xs text-white disabled:opacity-60"
          >
            {t('catalog.actions.confirmDelete')}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="px-2 py-1 text-xs text-text-muted"
          >
            {t('catalog.actions.cancel')}
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => void save()}
              disabled={create.isPending || update.isPending || draft.name_de.trim() === ''}
              className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
            >
              {t('catalog.actions.save')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onDirtyChange(false);
              onCancel();
            }}
            className="px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('catalog.actions.discard')}
          </button>
          {canEdit && processId !== null && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="ml-auto px-3 py-1.5 text-sm text-text-muted hover:text-status-fail"
            >
              {t('catalog.actions.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
